import { useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContext } from '@context/AuthContext';
import { FarmContext } from '@context/FarmContext';
import { appRoutes } from '@routes/appRoutes';
import { navigationItems, navigationSections } from './config';
import { getFirstAreaRoute, getNavigationArea, getVisibleNavigationItems } from './utils';

const LAST_MANAGEMENT_PATH_KEY = 'mecbov.navigation.lastManagementPath';

function readLastManagementPath(): string {
  try {
    return window.sessionStorage.getItem(LAST_MANAGEMENT_PATH_KEY) ?? appRoutes.dashboard;
  } catch {
    return appRoutes.dashboard;
  }
}

function writeLastManagementPath(path: string) {
  try {
    window.sessionStorage.setItem(LAST_MANAGEMENT_PATH_KEY, path);
  } catch {
    // Armazenamento indisponível: voltar à Gestão cai no Dashboard.
  }
}

/** Hook da rota inicial do Financeiro para o usuário atual (null = sem acesso). */
export function useFinancialEntryRoute(): string | null {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { farm, licenseMode, licenseFeatures } = useContext(FarmContext);
  return useMemo(() => {
    const visible = getVisibleNavigationItems(navigationItems, {
      isAuthenticated,
      user,
      farm,
      licenseMode,
      licenseFeatures,
    });
    return getFirstAreaRoute(visible, navigationSections, 'financial');
  }, [farm, isAuthenticated, licenseFeatures, licenseMode, user]);
}

/**
 * Seletor de área Gestão / Financeiro. Reabre a última tela da Gestão desta
 * aba; a seleção compartilhada retoma a última fazenda concreta ao sair de
 * Todas. Some quando o usuário não tem nenhuma tela do Financeiro.
 */
export function AreaSwitch({ placement }: { placement: 'topbar' | 'sidebar' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const financialEntry = useFinancialEntryRoute();
  const area = getNavigationArea(location.pathname);

  useEffect(() => {
    if (area === 'management') {
      writeLastManagementPath(`${location.pathname}${location.search}`);
    }
  }, [area, location.pathname, location.search]);

  if (!financialEntry) {
    return null;
  }

  return (
    <SwitchGroup role="group" aria-label="Área do sistema" data-placement={placement}>
      <SwitchButton
        type="button"
        aria-pressed={area === 'management'}
        onClick={() => {
          if (area !== 'management') void navigate(readLastManagementPath());
        }}
      >
        Gestão
      </SwitchButton>
      <SwitchButton
        type="button"
        aria-pressed={area === 'financial'}
        onClick={() => {
          if (area !== 'financial') void navigate(financialEntry);
        }}
      >
        Financeiro
      </SwitchButton>
    </SwitchGroup>
  );
}

const SwitchGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);

  &[data-placement='topbar'] {
    @media (max-width: 640px) {
      display: none;
    }
  }

  &[data-placement='sidebar'] {
    width: 100%;
    margin-bottom: 12px;
    background: rgba(26, 77, 58, 0.08);

    @media (min-width: 641px) {
      display: none;
    }
  }
`;

const SwitchButton = styled.button`
  flex: 1;
  min-height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  [data-placement='topbar'] & {
    color: #ffffff;
  }

  [data-placement='sidebar'] & {
    color: #1a4d3a;
  }

  &[aria-pressed='true'] {
    background: #ffffff;
    color: #1a4d3a;
  }

  &:focus-visible {
    outline: 2px solid #b7e4c7;
    outline-offset: 2px;
  }
`;
